// Scenarios — what-if projection over EventData.
//
// A scenario is a *projection function*: given the user's plan + a scenario
// configuration, produce a new EventData where ticket/sponsor quantities are
// scaled by fill rates, add-ons may be mass-toggled, and costs are scaled by
// a multiplier. The downstream calc / breakeven functions then operate on
// the projected data without knowing a scenario was applied.

import type {
  Addon,
  AddonsScenario,
  CostItem,
  CustomScenario,
  EventData,
  Scenario,
  ScenarioName,
  SponsorTier,
  TicketTier,
} from './types';

// Built-in preset configurations. Each preset is a single global rate per
// category — when applied, every tier picks up the same fill rate. Custom
// is the only mode that holds per-tier overrides.
export type PresetConfig = {
  tickets: number;       // fill rate for every ticket tier
  sponsors: number;      // fill rate for every sponsor tier
  addons: AddonsScenario;
  costMultiplier: number;
};

export const PRESETS: Record<Exclude<ScenarioName, 'custom'>, PresetConfig> = {
  pessimistic: {
    tickets: 0.6,
    sponsors: 0.75,
    addons: 'as-planned',
    costMultiplier: 1.1,
  },
  realistic: {
    tickets: 1.0,
    sponsors: 1.0,
    addons: 'as-planned',
    costMultiplier: 1.0,
  },
  optimistic: {
    tickets: 1.1,
    sponsors: 1.0,
    addons: 'all-on',
    costMultiplier: 0.95,
  },
};

export const SCENARIO_LABELS: Record<ScenarioName, string> = {
  pessimistic: 'Pessimistic',
  realistic: 'Realistic',
  optimistic: 'Optimistic',
  custom: 'Custom',
};

export function emptyCustom(): CustomScenario {
  return {
    ticketFill: {},
    sponsorFill: {},
    addonsEnabled: 'as-planned',
    costMultiplier: 1.0,
  };
}

export function defaultScenario(): Scenario {
  return { active: 'realistic', custom: emptyCustom() };
}

/**
 * Effective per-tier or per-category configuration for the active scenario.
 * Centralised so the panel UI and projection share one source of truth.
 */
export function resolveTicketFill(s: Scenario, tierId: string): number {
  if (s.active === 'custom') return clampFill(s.custom.ticketFill[tierId] ?? 1.0);
  return PRESETS[s.active].tickets;
}

export function resolveSponsorFill(s: Scenario, tierId: string): number {
  if (s.active === 'custom') return clampFill(s.custom.sponsorFill[tierId] ?? 1.0);
  return PRESETS[s.active].sponsors;
}

export function resolveAddons(s: Scenario): AddonsScenario {
  return s.active === 'custom' ? s.custom.addonsEnabled : PRESETS[s.active].addons;
}

export function resolveCostMultiplier(s: Scenario): number {
  return s.active === 'custom'
    ? clampCost(s.custom.costMultiplier)
    : PRESETS[s.active].costMultiplier;
}

const clampFill = (n: number) => Math.min(1.5, Math.max(0, n));
const clampCost = (n: number) => Math.min(2.0, Math.max(0.5, n));

/**
 * Convert a preset to a fully-materialised custom scenario seeded with
 * per-tier rates that match the preset's globals. Used when the user
 * "drops into" custom mode by moving any slider.
 */
export function freezePresetIntoCustom(
  data: EventData,
  fromName: Exclude<ScenarioName, 'custom'>,
): CustomScenario {
  const p = PRESETS[fromName];
  const ticketFill: Record<string, number> = {};
  const sponsorFill: Record<string, number> = {};
  for (const t of data.tickets) ticketFill[t.id] = p.tickets;
  for (const s of data.sponsors) sponsorFill[s.id] = p.sponsors;
  return {
    ticketFill,
    sponsorFill,
    addonsEnabled: p.addons,
    costMultiplier: p.costMultiplier,
  };
}

/**
 * Apply the active scenario to event data. Returns a new EventData; the
 * input is not mutated. Tier ids are preserved so downstream code that
 * keys off them (e.g. fill-rate sliders) keeps working.
 */
export function applyScenario(data: EventData): EventData {
  const s = data.scenario;

  const tickets: TicketTier[] = data.tickets.map((t) => ({
    ...t,
    qty: Math.round(t.qty * resolveTicketFill(s, t.id)),
  }));

  const sponsors: SponsorTier[] = data.sponsors.map((sp) => ({
    ...sp,
    qty: Math.round(sp.qty * resolveSponsorFill(s, sp.id)),
  }));

  const addonsMode = resolveAddons(s);
  const addons: Addon[] = data.addons.map((a) => {
    if (addonsMode === 'all-on') return { ...a, enabled: true };
    if (addonsMode === 'all-off') return { ...a, enabled: false };
    return a;
  });

  const cm = resolveCostMultiplier(s);
  const costs = (Object.keys(data.costs) as Array<keyof EventData['costs']>).reduce(
    (acc, key) => {
      acc[key] = data.costs[key].map(
        (item): CostItem => ({ ...item, amount: item.amount * cm }),
      );
      return acc;
    },
    {} as EventData['costs'],
  );

  return { ...data, tickets, sponsors, addons, costs };
}
